// 取り込みのために受け取ったCSVファイル群を、サーバーへ送れるテーブル別の行へ変換するモジュール。
// zip の展開とファイルの読み込みは画面側で済ませ、ここには「ファイル名と本文」だけが渡ってくる。
// 参照先の実在やIDの衝突はサーバーが実データを見て判断するため、ここでは書式・列・値の型だけを点検する。
//
// どのマスタのデータかは本文1行目の識別行だけで決める。ファイル名は判定に使わず、不備を伝えるときの
// 目印としてだけ持ち回る。列構成が完全に同一のマスタが存在し、名前や列の並びでは区別できないため。
//
// 1件目の不備で止めず全ファイルを最後まで走査する。利用者が1回の修正でまとめて直せるようにするため。

import type { SliceKey } from "../../../../api/dbSlice";
import type { CsvParseError, CsvParsedRow } from "../../../../util/csvIo";
import { csvParseStrict } from "../../../../util/csvIo";
import { embeddedValueTextOf } from "../../../../util/embeddedValueText";
import { limitedListOf } from "../../../../util/limitedList";
import type { MasterImportCellValue } from "../../../../schema/console/master_import";
import type { MasterCsvColumn } from "./masterCsvColumns";
import { csvColumnsOf, valueOfCell } from "./masterCsvColumns";
import { ALL_MASTER_CSV_COLLECTION_KEYS, MASTER_CSV_TABLE_ROW_LABEL } from "./masterCsvTargets";

// 取り込み対象として受け取った1ファイル分。zip から取り出したものも単体投入のものも同じ形にして渡す。
// fileName はどのファイルの話かを利用者へ伝えるためだけに使い、テーブルの判定には使わない。
export type MasterCsvSourceFile = { fileName: string; text: string };

// 取り込む1行分。lineNumber は利用者が表計算ソフトで開いたときの行番号に合わせる。
// 識別行が1行目・ヘッダ行が2行目のため、最初のデータ行は3行目になる。
export type MasterCsvRow = { lineNumber: number; values: Record<string, MasterImportCellValue> };

// 1テーブル分の取り込み内容。どのファイル由来かを残し、エラー表示で元のファイルを指し示せるようにする。
export type MasterCsvTable = { collectionKey: SliceKey; sourceFileName: string; rows: MasterCsvRow[] };

// 利用者へそのまま見せられる不備1件。行や列を特定できない不備では該当欄が null になる。
export type MasterCsvFileError = { fileName: string; lineNumber: number | null; columnName: string | null; message: string };

// 失敗時の hiddenErrorCount は、1ファイルあたりの上限で切り捨てた不備の件数を全ファイル分合計したもの。
// 表示上の上限による切り捨てはここに含まない。表示側(masterCsvDialogState)が別に数えて合算する。
export type MasterCsvFilesResult =
  | { ok: true; tables: MasterCsvTable[] }
  | { ok: false; errors: MasterCsvFileError[]; hiddenErrorCount: number };

// 1ファイルにつき利用者へ見せる不備の上限。これを超えた分は件数だけを伝える。
export const MAX_ERRORS_PER_FILE = 100;

// 1ファイルにつき取り込みを許すデータ行数の上限。
// ブラウザ上で全件を展開してメモリに載せるため、意図しない巨大ファイルで画面が固まるのを防ぐ。
const MAX_DATA_ROW_COUNT = 50000;

// 1ファイルにつき解析を許す文字数の上限。
// 行数の上限は解析を終えてからでないと分からないため、それより手前で受け取った本文の長さだけを見て打ち切る。
// 解析しきってから判定したのでは、止めたかった重い処理が先に終わってしまうため。
const MAX_FILE_TEXT_LENGTH = 20000000;

// 取り込み先になり得るテーブル1つ分の見取り図。列の並びはCSVの列順そのもの。
type MasterCsvTableSpec = { collectionKey: SliceKey; columns: MasterCsvColumn[]; columnNames: string[] };

const TABLE_SPECS: readonly MasterCsvTableSpec[] = ALL_MASTER_CSV_COLLECTION_KEYS.map((collectionKey) => {
  const columns = csvColumnsOf(collectionKey);
  return { collectionKey, columns, columnNames: columns.map((column) => column.name) };
});

// 識別行に書ける全テーブル名を並べた文。何を書けばよいか分からなくなった利用者への案内に添える。
const TABLE_NAME_LIST_TEXT = TABLE_SPECS.map((spec) => spec.collectionKey).join(", ");

// 識別行に書かれたテーブル名から取り込み先を決める。綴りの違いは別物として扱う。
function specOfTableName(tableName: string): MasterCsvTableSpec | undefined {
  return TABLE_SPECS.find((spec) => spec.collectionKey === tableName);
}

// 列名の並びが完全に一致するかを見る。順序が違うだけでも別物として扱う。
function isSameColumnOrder(expectedNames: readonly string[], actualNames: readonly string[]): boolean {
  const isSameLength = expectedNames.length === actualNames.length;
  if (isSameLength) {
    return expectedNames.every((name, index) => name === actualNames[index]);
  }
  return false;
}

// 読み取ったセルの中身を案内文へ埋め込む。空のセルは鍵括弧では伝わらないため言葉で示す。
function readCellText(cell: string): string {
  if (cell === "") {
    return "空のセル";
  }
  return `「${embeddedValueTextOf(cell)}」`;
}

// 1行目に識別行が無い場合の案内文。1行目に何を書けばよいかをそのまま書き写せる形で示す。
function missingTableRowMessage(): string {
  const howToFix = `1行目に「${MASTER_CSV_TABLE_ROW_LABEL},マスタ名」の2つのセルを書き、2行目を列名の行にしてください。`;
  return `1行目にどのマスタかを示す識別行がありません。${howToFix}マスタ名に使えるのは ${TABLE_NAME_LIST_TEXT} です。`;
}

// 1行目の1列目が決められた見出し文字列でない場合の案内文。
// 識別行を消したまま列名の行から始めてしまった場合もここに来るため、書き足し方まで示す。
function notTableRowMessage(labelCell: string): string {
  const howToFix = `1行目に「${MASTER_CSV_TABLE_ROW_LABEL},マスタ名」の2つのセルを書き、列名の行はその次の行にしてください。`;
  const readText = `読み取った1行目の1列目は${readCellText(labelCell)}です。`;
  return `1行目がどのマスタかを示す識別行になっていません。${readText}${howToFix}マスタ名に使えるのは ${TABLE_NAME_LIST_TEXT} です。`;
}

// 1行目の2列目が空の場合の案内文。
function missingTableNameMessage(): string {
  return `1行目の2列目にマスタ名がありません。「${MASTER_CSV_TABLE_ROW_LABEL},マスタ名」の形で書いてください。マスタ名に使えるのは ${TABLE_NAME_LIST_TEXT} です。`;
}

// 1行目の2列目が取り込み対象のマスタ名でない場合の案内文。
function unknownTableNameMessage(tableNameCell: string): string {
  return `1行目のマスタ名「${embeddedValueTextOf(tableNameCell)}」は取り込めるマスタの名前ではありません。マスタ名に使えるのは ${TABLE_NAME_LIST_TEXT} です。`;
}

// 識別行だけがあり、列名の行が続いていない場合の案内文。
function missingHeaderRowMessage(spec: MasterCsvTableSpec): string {
  return `2行目に列名の行がありません。1行目の識別行の次の行に ${spec.columnNames.join(", ")} を並べてください。`;
}

// テーブルは決まったのにヘッダが違う場合の案内文。何を直せばよいかを列名で示す。
function headerMismatchMessage(spec: MasterCsvTableSpec, headerNames: readonly string[]): string {
  const missingNames = spec.columnNames.filter((name) => !headerNames.includes(name));
  const extraNames = headerNames.filter((name) => !spec.columnNames.includes(name));
  // 読み取ったヘッダの列名はいくらでも長くなりうるため、並べる前に1つずつ切り詰める。
  // 連結してから全体を切り詰めると、どの列名の話なのかが読み取れなくなる。
  const extraNameTexts = extraNames.map((name) => embeddedValueTextOf(name));
  const expectedText = `正しい列の並びは ${spec.columnNames.join(", ")} です。`;
  const isOrderOnly = missingNames.length === 0 && extraNameTexts.length === 0;
  if (isOrderOnly) {
    return `${spec.collectionKey} の列の並び順が違います。${expectedText}`;
  }
  const differences: string[] = [];
  if (missingNames.length > 0) {
    differences.push(`足りない列は ${missingNames.join(", ")} です。`);
  }
  if (extraNameTexts.length > 0) {
    differences.push(`余分な列は ${extraNameTexts.join(", ")} です。`);
  }
  return `${spec.collectionKey} の列が想定と違います。${differences.join("")}${expectedText}`;
}

// 型変換できなかったセルの案内文。利用者が該当セルを見つけて直せるよう、値と理由を並べて示す。
function cellErrorMessage(columnName: string, cell: string, reason: string): string {
  return `列「${columnName}」の値「${embeddedValueTextOf(cell)}」は取り込めません。${reason}。`;
}

// CSV の書式そのものの不備。
// 解析に失敗した時点では列名を決められないため、何列目かは案内文に添える。
// 行番号は必ず rowNumber を使う。利用者が表計算ソフトで開いたときの行と一致するのはこちらで、
// テキスト上の物理行(textLine)はセル内の改行の分だけずれるため画面には出さない。
function fileErrorOfParseError(fileName: string, parseError: CsvParseError): MasterCsvFileError {
  if (parseError.column === null) {
    return { fileName, lineNumber: parseError.rowNumber, columnName: null, message: parseError.message };
  }
  return { fileName, lineNumber: parseError.rowNumber, columnName: null, message: `${parseError.column}列目: ${parseError.message}` };
}

type MasterCsvValues = Record<string, MasterImportCellValue>;

type ConvertedRow = { values: MasterCsvValues; errors: MasterCsvFileError[] };

// 1行分のセルを列の型に沿った値へ変換する。読めなかったセルは行番号と列名を付けて集める。
function convertedRowOf(
  fileName: string,
  columns: readonly MasterCsvColumn[],
  cellsByColumnName: Record<string, string>,
  lineNumber: number,
): ConvertedRow {
  const values: MasterCsvValues = {};
  const errors: MasterCsvFileError[] = [];
  for (const column of columns) {
    const cell = cellsByColumnName[column.name] ?? "";
    const parsedCell = valueOfCell(cell, column);
    if (parsedCell.ok) {
      values[column.name] = parsedCell.value;
    } else {
      const message = cellErrorMessage(column.name, cell, parsedCell.reason);
      errors.push({ fileName, lineNumber, columnName: column.name, message });
    }
  }
  return { values, errors };
}

// 1ファイルを読んだ結果。テーブルを決められなかった場合は table が null になる。
type FileOutcome = { table: MasterCsvTable | null; errors: MasterCsvFileError[] };

// ファイル1つを丸ごと止める不備を、そのまま返せる形にする。行を特定できない不備では行番号を空にする。
function stoppedOutcomeOf(fileName: string, lineNumber: number | null, message: string): FileOutcome {
  return { table: null, errors: [{ fileName, lineNumber, columnName: null, message }] };
}

// 解析済みの全データ行を取り込み用の行へ変換する。
//
// lineNumber には csvParseStrict が返した rowNumber をそのまま使う。この番号は、
// 空行を含めて数えた記録の並び順から作られており、表計算ソフトで開いたときの行に一致する。
//
// 識別行とヘッダ行だけのファイルは、あえて0件の成功として通す。取り込みは既存の行を残すマージであり
// 0件の適用は何もしないのと同じで害が無い一方、雛形として配ったファイルが取り込めない方が困るため。
function outcomeOfParsedRows(
  fileName: string,
  spec: MasterCsvTableSpec,
  parsedRows: readonly CsvParsedRow[],
): FileOutcome {
  const rows: MasterCsvRow[] = [];
  const errors: MasterCsvFileError[] = [];
  for (const parsedRow of parsedRows) {
    const lineNumber = parsedRow.rowNumber;
    const converted = convertedRowOf(fileName, spec.columns, parsedRow.values, lineNumber);
    rows.push({ lineNumber, values: converted.values });
    errors.push(...converted.errors);
  }
  return { table: { collectionKey: spec.collectionKey, sourceFileName: fileName, rows }, errors };
}

// データ行数が上限を超えている場合の案内文。上限と実際の行数の両方を示す。
function rowCountOverLimitMessage(rowCount: number): string {
  return `データ行数が上限を超えています。上限は${MAX_DATA_ROW_COUNT}行ですが、このファイルは${rowCount}行あります。ファイルを分割してください。`;
}

// ファイルの文字数が上限を超えている場合の案内文。上限と実際の文字数の両方を示す。
function textLengthOverLimitMessage(textLength: number): string {
  return `ファイルの文字数が上限を超えています。上限は${MAX_FILE_TEXT_LENGTH}文字ですが、このファイルは${textLength}文字あります。ファイルを分割してください。`;
}

// 1ファイルを、文字数・書式・行数・識別行・ヘッダ照合・型変換の順に読み進める。
// 先の段階で止まった場合は後段へ進まない。後段の指摘が前段の不備の言い換えになり、同じ原因を何度も見せるため。
//
// テーブルの特定は1行目の識別行だけで行う。ファイル名は不備を伝えるときの目印にしか使わない。
function outcomeOfSourceFile(sourceFile: MasterCsvSourceFile): FileOutcome {
  const fileName = sourceFile.fileName;
  if (sourceFile.text.length > MAX_FILE_TEXT_LENGTH) {
    return stoppedOutcomeOf(fileName, null, textLengthOverLimitMessage(sourceFile.text.length));
  }
  const parsed = csvParseStrict(sourceFile.text);
  if (!parsed.ok) {
    return { table: null, errors: parsed.errors.map((parseError) => fileErrorOfParseError(fileName, parseError)) };
  }
  if (parsed.rows.length > MAX_DATA_ROW_COUNT) {
    return stoppedOutcomeOf(fileName, null, rowCountOverLimitMessage(parsed.rows.length));
  }
  const tableRow = parsed.leadRow;
  if (tableRow === null) {
    return stoppedOutcomeOf(fileName, null, missingTableRowMessage());
  }
  // 識別行の不備を指し示す行番号。識別行より前に空行があると1行目とは限らないため、解析結果が数えた行を使う。
  const tableRowLineNumber = tableRow.rowNumber;
  const labelCell = tableRow.cells[0] ?? "";
  if (labelCell !== MASTER_CSV_TABLE_ROW_LABEL) {
    return stoppedOutcomeOf(fileName, tableRowLineNumber, notTableRowMessage(labelCell));
  }
  const tableNameCell = tableRow.cells[1] ?? "";
  if (tableNameCell === "") {
    return stoppedOutcomeOf(fileName, tableRowLineNumber, missingTableNameMessage());
  }
  const spec = specOfTableName(tableNameCell);
  if (spec === undefined) {
    return stoppedOutcomeOf(fileName, tableRowLineNumber, unknownTableNameMessage(tableNameCell));
  }
  // ヘッダの不備を指し示す行番号。空行が挟まると2行目とは限らないため、解析結果が数えた行を使う。
  const headerLineNumber = parsed.headerRowNumber;
  if (headerLineNumber === null) {
    return stoppedOutcomeOf(fileName, null, missingHeaderRowMessage(spec));
  }
  const isExpectedHeader = isSameColumnOrder(spec.columnNames, parsed.headers);
  if (isExpectedHeader) {
    return outcomeOfParsedRows(fileName, spec, parsed.rows);
  }
  return stoppedOutcomeOf(fileName, headerLineNumber, headerMismatchMessage(spec, parsed.headers));
}

// 1ファイル分の不備が多すぎると際限なく溜め込まれないよう、先頭だけを残し、削った件数を返す。
// 打ち切りの案内文はここでは作らない。案内は不備の行に混ぜず、呼び出し側が全ファイル分の件数を合算してから利用者へ伝える。
type LimitedFileErrors = { errors: MasterCsvFileError[]; hiddenCount: number };

function limitedErrorsOf(errors: readonly MasterCsvFileError[]): LimitedFileErrors {
  const limited = limitedListOf(errors, MAX_ERRORS_PER_FILE);
  return { errors: limited.items, hiddenCount: limited.hiddenCount };
}

// 1ファイルを読んで不備を上限で切り詰めた結果。ファイル名は、テーブルの重複を指摘する案内文で使う。
type LimitedFileOutcome = { fileName: string; table: MasterCsvTable | null; errors: MasterCsvFileError[]; hiddenCount: number };

function limitedOutcomeOf(sourceFile: MasterCsvSourceFile): LimitedFileOutcome {
  const outcome = outcomeOfSourceFile(sourceFile);
  const limited = limitedErrorsOf(outcome.errors);
  return { fileName: sourceFile.fileName, table: outcome.table, errors: limited.errors, hiddenCount: limited.hiddenCount };
}

// 受け取ったファイル群をテーブル別の行へ変換する。
// 1件でも不備があれば取り込み内容は返さず、不備の一覧だけを返す。中途半端に一部だけ送らないため。
export function tablesOfSourceFiles(sourceFiles: readonly MasterCsvSourceFile[]): MasterCsvFilesResult {
  const limitedOutcomes = sourceFiles.map((sourceFile) => limitedOutcomeOf(sourceFile));
  const tables: MasterCsvTable[] = [];
  const errors: MasterCsvFileError[] = [];
  for (const limitedOutcome of limitedOutcomes) {
    errors.push(...limitedOutcome.errors);
    const table = limitedOutcome.table;
    if (table !== null) {
      const alreadyTaken = tables.find((taken) => taken.collectionKey === table.collectionKey);
      if (alreadyTaken === undefined) {
        tables.push(table);
      } else {
        // 案内文へ埋め込むファイル名は zip のエントリ名がそのまま入り、書き手が任意の長さにできるため切り詰める。
        // 不備一覧の「出どころ」欄に出す fileName フィールド自体はここでは変えない。
        const takenFileNameText = embeddedValueTextOf(alreadyTaken.sourceFileName);
        const duplicateFileNameText = embeddedValueTextOf(limitedOutcome.fileName);
        const message = `${table.collectionKey} のファイルが2つあります。${takenFileNameText} と ${duplicateFileNameText} のどちらか一方だけにしてください。`;
        errors.push({ fileName: limitedOutcome.fileName, lineNumber: null, columnName: null, message });
      }
    }
  }
  const hiddenErrorCount = limitedOutcomes.reduce((total, limitedOutcome) => total + limitedOutcome.hiddenCount, 0);
  if (errors.length > 0) {
    return { ok: false, errors, hiddenErrorCount };
  }
  return { ok: true, tables };
}
